/**
 * Live suggestions in the search overlay.
 *
 * Everything here is an enhancement. The field sits in a real form pointed at
 * /search, so with this file blocked a query still works — it just takes a page
 * load. With it, typing swaps the trending terms for suggestions.
 *
 * Requests are debounced and the in-flight one is aborted when the next key
 * lands, so a fast typist never sees results for a query they have moved past.
 */
const DEBOUNCE = 250;
const MIN_LENGTH = 2;

class SearchOverlay extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.trending = this.querySelector('[data-trending]');
    this.results = this.querySelector('[data-results]');
    this.empty = this.querySelector('[data-empty]');
    this.emptyTemplate = this.querySelector('[data-empty-template]');
    this.notSold = (this.dataset.notSold || '').split('|').map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!this.input || !this.results) return;

    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.suggest(this.input.value.trim()), DEBOUNCE);
    });
  }

  show(showResults) {
    this.results.hidden = !showResults;
    if (this.empty) this.empty.hidden = true;
    if (this.trending) this.trending.hidden = showResults;
  }

  /* Nothing matched. The template carries both lines; the query decides
     which — a stone we do not make gets the honest one and "here's why". */
  showEmpty(query) {
    if (!this.empty || !this.emptyTemplate) return;
    const clone = this.emptyTemplate.content.cloneNode(true);
    const notSold = this.notSold.some((stone) => query.toLowerCase().includes(stone));
    clone.querySelectorAll('[data-empty-not-sold]').forEach((n) => (n.hidden = !notSold));
    clone.querySelectorAll('[data-empty-generic]').forEach((n) => (n.hidden = notSold));
    clone.querySelectorAll('.search-empty__head, .search-empty__line, [href]').forEach((n) => {
      if (n.textContent.includes('%terms%')) n.textContent = n.textContent.split('%terms%').join(query);
      if (n.href && n.href.includes('%25terms%25')) n.href = n.href.split('%25terms%25').join(encodeURIComponent(query));
    });
    this.empty.replaceChildren(clone);
    this.results.hidden = true;
    this.empty.hidden = false;
    if (this.trending) this.trending.hidden = true;
  }

  async suggest(query) {
    if (query.length < MIN_LENGTH) {
      this.show(false);
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();

    const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json`
      + `?q=${encodeURIComponent(query)}`
      + `&resources[type]=product,collection,page&resources[limit]=4`;

    let data;
    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.status);
      data = await response.json();
    } catch (error) {
      /* An aborted request is the expected case, not a failure. Anything else
         leaves the field working as a plain form. */
      if (error.name !== 'AbortError') this.show(false);
      return;
    }

    this.render(data.resources?.results || {});
  }

  render(results) {
    const groups = [
      ['products', this.dataset.labelProducts || 'Products'],
      ['collections', this.dataset.labelCollections || 'Collections'],
      ['pages', this.dataset.labelPages || 'Answers'],
    ];

    const html = groups
      .filter(([key]) => results[key]?.length)
      .map(([key, title]) => {
        const items = results[key]
          .map((item) => {
            const bits = [];
            /* Predictive search hands back a bare number. Indian grouping,
               the same way the wishlist prints it. */
            if (key === 'products' && item.price) bits.push('₹' + Math.round(Number(item.price)).toLocaleString('en-IN'));
            if (key === 'products') bits.push(item.available === false ? this.dataset.labelMadeFor : this.dataset.labelReady);
            const meta = bits.length ? `<span class="search__result-meta">${bits.filter(Boolean).join(' · ')}</span>` : '';
            return `<a class="search__result" href="${item.url}">
                <span class="search__result-name">${item.title}</span>${meta}
              </a>`;
          })
          .join('');
        return `<div><p class="search__group-title">${title}</p>${items}</div>`;
      })
      .join('');

    if (!html) {
      this.showEmpty(this.input.value.trim());
      return;
    }
    this.results.innerHTML = html;
    this.show(true);
  }
}

customElements.define('search-overlay', SearchOverlay);
